import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {
  cancelableResource,
  DateService,
  debouncedSignal,
  IsoDate,
} from '@softline/core';
import { AvatarComponent, BadgeComponent, ButtonComponent, CardBodyComponent, CardComponent, CardDescriptionComponent, CardHeaderComponent, CardLeadingComponent, CardTitleComponent, CardTrailingComponent, EmptyStateComponent, IconComponent, ItemComponent, ItemDescriptionComponent, ItemLeadingComponent, ItemTitleComponent, ItemTrailingComponent, LoadingSkeletonComponent, modalFn, SearchInputComponent, SoftSlotDirective } from '@softline/ui-core';
import { KpiComponent } from '@softline/ui-core/progress';
import { DsTabComponent, DsTabsComponent } from '@softline/ui-core/tabs';
import {
  MASTERDATA_CONSTRUCTION_SITE,
  MASTERDATA_DEFAULT_PAGE_SIZE,
  MasterdataCustomer,
  MasterdataFilter,
  MasterdataService,
} from '@softapps/core/masterdata';
import { Subscription } from 'rxjs';
import { CreditLimitCardComponent } from '../credit-limit-card/credit-limit-card.component';
import { CustomerStatisticsService } from '../../services/customer-statistics.service';
import { CustomerService } from '../../services/customer.service';
import { SiteMapThumbComponent } from '../site-map-thumb/site-map-thumb.component';
import { CustomerPanelInput } from './customer-panel-input';
import type { ConstructionSiteFormComponent } from '../construction-site-form/construction-site-form.component';

/** Shift an ISO date (YYYY-MM-DD) by `years`, keeping month/day. */
function shiftYear(iso: string, years: number): IsoDate {
  const [y, m, d] = iso.split('-').map(Number);
  const shifted = new Date(Date.UTC(y + years, (m ?? 1) - 1, d ?? 1));
  return shifted.toISOString().slice(0, 10) as IsoDate;
}

@Component({
  selector: 'soft-customer-info-side-panel',
  templateUrl: './customer-info-side-panel.component.html',
  styleUrls: ['./customer-info-side-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DsTabsComponent,
    DsTabComponent,
    AvatarComponent,
    BadgeComponent,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemDescriptionComponent,
    ItemTrailingComponent,
    LoadingSkeletonComponent,
    SearchInputComponent,
    KpiComponent,
    CardComponent,
    CardHeaderComponent,
    CardLeadingComponent,
    CardTitleComponent,
    CardDescriptionComponent,
    CardTrailingComponent,
    CardBodyComponent,
    SiteMapThumbComponent,
    EmptyStateComponent,
    CreditLimitCardComponent,
  ],
  providers: [
    CustomerService,
    // Panel-owned state — provided here so the panel is self-contained wherever
    // it is rendered (search modal, sales pages), not reliant on the host to
    // provide them. (MasterdataService / CustomerRepository are
    // providedIn: 'root', so they need no entry.)
    CustomerStatisticsService,
  ],
})
export class CustomerInfoSidePanelComponent {
  private readonly customerService = inject(CustomerService);
  private readonly masterdata = inject(MasterdataService);
  private readonly customerStatisticsService = inject(CustomerStatisticsService);
  private readonly dateService = inject(DateService);

  /** The JxBrowser embedded views (/embedded/...) cannot navigate anywhere, so
      external links (Google Maps) are hidden there — everywhere else they stay. */
  private readonly isEmbeddedHost = inject(Router).url.startsWith('/embedded');

  private readonly modal = modalFn();

  /** Customer detail, contacts, collectors, conditions, cards, fuel cards and
      notes are owned by the service; re-exposed here so the template + this
      component's computeds keep their existing names. */
  readonly customerDetail = this.customerService.customerDetail;
  readonly customerDetailLoading = computed(
    () => this.customerService.customerDetailLoading() || this.awaitingCustomerId()
  );
  readonly customerDetailError = this.customerService.customerDetailError;
  readonly customerContacts = this.customerService.customerContacts;
  readonly customerContactsLoading = this.customerService.customerContactsLoading;
  readonly customerContactsError = this.customerService.customerContactsError;
  readonly customerCollectors = this.customerService.customerCollectors;
  readonly customerCollectorsLoading = this.customerService.customerCollectorsLoading;
  readonly customerCollectorsError = this.customerService.customerCollectorsError;
  readonly customerConditions = this.customerService.customerConditions;
  readonly customerConditionsLoading = this.customerService.customerConditionsLoading;
  readonly customerConditionsError = this.customerService.customerConditionsError;
  readonly customerCards = this.customerService.customerCards;
  readonly customerCardsLoading = this.customerService.customerCardsLoading;
  readonly customerCardsError = this.customerService.customerCardsError;
  readonly customerFuelCards = this.customerService.customerFuelCards;
  readonly customerFuelCardsLoading = this.customerService.customerFuelCardsLoading;
  readonly customerFuelCardsError = this.customerService.customerFuelCardsError;
  readonly customerNotes = this.customerService.customerNotes;
  readonly customerNotesLoading = this.customerService.customerNotesLoading;
  readonly customerNotesError = this.customerService.customerNotesError;

  /** Selected customer driving the data fetching. */
  readonly customer = input<CustomerPanelInput | null>(null);

  /** Drop the standalone card chrome (border + radius) when the panel is
      embedded in a host that already provides the card surface. */
  readonly flush = input<boolean>(false);
  readonly dateFrom = input<IsoDate | null>(null);
  readonly dateTo = input<IsoDate | null>(null);

  /** The document the host page currently has loaded. When set, the Kreditlimit
      section switches to the document-bound check — it reports the backend's
      assessment and keeps the current document out of the customer balance
      instead of counting it twice. */
  readonly documentId = input<string | null>(null);
  /** Any value that changes when the bound document changed (e.g. the last-saved
      timestamp) — refetches the credit limit, which goes stale with every
      position edit. */
  readonly documentRevision = input<unknown>(null);

  readonly closePanel = output<void>();
  readonly createDocument = output<void>();
  /** Emitted when a construction site is clicked — opens the create-Beleg
   *  wizard with that site pre-selected. */
  readonly createDocumentForSite = output<MasterdataCustomer>();
  readonly openCustomerFile = output<void>();
  /** A construction site was created or changed — carries its `customerId`,
   *  usable wherever a customer id is expected. */
  readonly constructionSiteSaved = output<string>();

  /** Panel tabs: 0 overview · 1 construction sites · 2 contacts · 3 master data ·
      4 conditions · 5 cards · 6 collectors */
  readonly panelTabIndex = signal<number>(0);

  /** Explicit per-section overrides set by the user; absent = use the default
      (expanded when the section has data, collapsed when empty). */
  private readonly expandedSections = signal<Record<string, boolean>>({});
  isSectionExpanded(key: string): boolean {
    return this.expandedSections()[key] ?? this.sectionHasData(key);
  }
  toggleSection(key: string): void {
    const expanded = this.isSectionExpanded(key);
    this.expandedSections.update((s) => ({ ...s, [key]: !expanded }));
  }

  /** Whether the contact section has any phone/mail/web value to show. */
  readonly contactHasData = computed(() => {
    const d = this.customerData();
    return !!(
      d?.phone1 ||
      d?.phone2 ||
      d?.directDial ||
      d?.fax ||
      d?.email ||
      d?.email2 ||
      d?.email3 ||
      d?.url
    );
  });

  /** Whether a section has content — drives the collapse-when-empty default. */
  sectionHasData(key: string): boolean {
    const s = this.customerMasterData();
    switch (key) {
      case 'contact':
        return this.contactHasData();
      case 'addresses':
        return !!this.customerData()?.street || !!this.customerDeliveryAddress();
      case 'customerGroup':
        return !!this.customerGroupLabel();
      case 'conditions':
        return !!s;
      case 'representatives':
        return (s?.representative1 ?? '—') !== '—' || (s?.representative2 ?? '—') !== '—';
      case 'tax':
        return (s?.taxVatId ?? '—') !== '—' || (s?.gln ?? '—') !== '—';
      case 'delivery':
        return (s?.deliveryNumber ?? '—') !== '—';
      case 'insurance':
        return (s?.insuranceIndicator ?? '—') !== '—';
      case 'totals':
        return (s?.summations?.length ?? 0) > 0 || (s?.terms?.length ?? 0) > 0;
      case 'other':
        return true;
      default:
        return true;
    }
  }

  /** Two-letter initials from a display name (e.g. "Alex Henderson" → "AH"). */
  initialsOf(name: string | null | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '—';
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase() || '—';
  }

  private readonly customerId = computed(() => {
    // Falsy ids (0/'') are placeholder values from document mappings, not real
    // customer-service ids — treat them as missing.
    const id = this.customer()?.id;
    return id ? String(id) : null;
  });

  /** A customer is bound but carries no usable id (e.g. a document response
   *  skeleton) — surface the loading state instead of a silent empty panel
   *  while the host resolves the id. */
  readonly awaitingCustomerId = computed(
    () => this.customer() != null && this.customerId() == null
  );

  /** Customer number / subnumber come from the loaded detail (the input carries
   *  only the id) — used to scope the construction-sites + statistics queries. */
  private readonly customerNumber = computed<number | null>(
    () => this.customerDetail()?.account?.number ?? null
  );
  private readonly customerSubNumber = computed<number>(
    () => this.customerDetail()?.account?.subnumber ?? 0
  );

  /** Barverkauf/Einmalkunde: customer-related figures (Kreditlimit, Umsatz/DB,
      Baustellen) pool all walk-in customers and are therefore not offered. */
  readonly isOneTimeCustomer = computed<boolean>(
    () => this.customer()?.oneTimeCustomer ?? false
  );

  /** Feed the selected customer id into the service so it (re)loads detail,
      contacts, collectors, conditions, cards, fuel cards and notes. Reset to the
      Übersicht tab on customer change — the data tabs (2..7) are hidden until the
      new detail loads, so a stale selected index would show empty content. */
  private readonly syncCustomerId = effect(() => {
    this.customerService.customerId.set(this.customerId());
    this.panelTabIndex.set(0);
    this.siteSearch.set('');
  });

  retryLoad(): void {
    const id = this.customerId();
    this.customerService.customerId.set(null);
    this.customerService.customerId.set(id);
  }

  retryConstructionSites(): void {
    this.loadConstructionSites(this.siteQuery());
  }

  /* ---- construction sites (masterdata, server-side search + paging) ---- */

  /** The term searches ALL of the customer's sites server-side — the masterdata
   *  query pages at {@link MASTERDATA_DEFAULT_PAGE_SIZE}, scrolling appends. */
  readonly siteSearch = signal<string | null>('');
  private readonly debouncedSiteSearch = debouncedSignal(this.siteSearch, 300);

  private readonly sites = signal<MasterdataCustomer[]>([]);
  readonly customerConstructionSites = this.sites.asReadonly();
  readonly constructionSitesTotal = signal<number | null>(null);
  readonly constructionSitesLoading = signal(false);
  readonly constructionSitesLoadingMore = signal(false);
  readonly constructionSitesError = signal(false);
  readonly constructionSitesHasMore = computed(() => {
    const total = this.constructionSitesTotal();
    return total != null && this.customerConstructionSites().length < total;
  });

  private sitesSubscription: Subscription | null = null;
  private sitesOffset = 0;

  private readonly siteQuery = computed<{
    customerNumber: number;
    term: string;
  } | null>(() => {
    if (this.isOneTimeCustomer()) return null;
    const number = this.customerNumber();
    if (number == null) return null;
    return {
      customerNumber: Number(number),
      term: (this.debouncedSiteSearch() ?? '').trim(),
    };
  });

  private readonly loadSitesOnQueryChange = effect(() => {
    const query = this.siteQuery();
    untracked(() => this.loadConstructionSites(query));
  });

  private readonly disposeSitesSubscription = inject(DestroyRef).onDestroy(() =>
    this.sitesSubscription?.unsubscribe()
  );

  private siteFilter(customerNumber: number): MasterdataFilter {
    return { type: 'numberEquals', name: 'customerNumber', value: customerNumber };
  }

  private loadConstructionSites(
    query: { customerNumber: number; term: string } | null
  ): void {
    this.sitesSubscription?.unsubscribe();
    this.sitesSubscription = null;
    this.constructionSitesLoadingMore.set(false);
    this.constructionSitesError.set(false);
    this.sitesOffset = 0;
    if (!query) {
      this.sites.set([]);
      this.constructionSitesTotal.set(0);
      this.constructionSitesLoading.set(false);
      return;
    }
    this.constructionSitesLoading.set(true);
    this.sitesSubscription = this.masterdata
      .searchWithCount$(MASTERDATA_CONSTRUCTION_SITE, query.term, {
        filter: this.siteFilter(query.customerNumber),
        limit: MASTERDATA_DEFAULT_PAGE_SIZE,
        offset: 0,
      })
      .subscribe({
        next: ({ items, totalCount }) => {
          this.sites.set(items);
          this.constructionSitesTotal.set(totalCount);
          this.sitesOffset = items.length;
          this.constructionSitesLoading.set(false);
        },
        error: () => {
          this.sites.set([]);
          this.constructionSitesTotal.set(null);
          this.constructionSitesLoading.set(false);
          this.constructionSitesError.set(true);
        },
      });
  }

  loadMoreConstructionSites(): void {
    if (
      this.constructionSitesLoading() ||
      this.constructionSitesLoadingMore() ||
      !this.constructionSitesHasMore()
    ) {
      return;
    }
    const query = this.siteQuery();
    if (!query) return;
    const offset = this.sitesOffset;
    this.constructionSitesLoadingMore.set(true);
    this.sitesSubscription?.unsubscribe();
    this.sitesSubscription = this.masterdata
      .searchWithCount$(MASTERDATA_CONSTRUCTION_SITE, query.term, {
        filter: this.siteFilter(query.customerNumber),
        limit: MASTERDATA_DEFAULT_PAGE_SIZE,
        offset,
      })
      .subscribe({
        next: ({ items, totalCount }) => {
          this.sites.update((current) => [...current, ...items]);
          this.constructionSitesTotal.set(totalCount);
          this.sitesOffset = offset + items.length;
          this.constructionSitesLoadingMore.set(false);
        },
        error: () => this.constructionSitesLoadingMore.set(false),
      });
  }

  /** Appends the next page when the panel body scrolls near its end while the
   *  Baustellen tab is active. */
  onPanelBodyScroll(event: Event): void {
    if (this.panelTabIndex() !== 1) return;
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 200) {
      this.loadMoreConstructionSites();
    }
  }

  private readonly statisticsResource = cancelableResource({
    params: () => {
      if (this.isOneTimeCustomer()) return null;
      const number = this.customerNumber();
      if (number == null) return null;
      const from = this.dateFrom() ?? this.defaultFrom();
      const to = this.dateTo() ?? this.dateService.now();
      return {
        number,
        subNumber: this.customerSubNumber(),
        primary: { from, to },
        // Comparison period: the same window shifted back one year (prior-YTD).
        reference: { from: shiftYear(from, -1), to: shiftYear(to, -1) },
      };
    },
    loader: async ({ params }) => {
      if (!params) return null;
      // Single request returns both the current period (series + totals) and
      // the comparison period (totals) — mirrors the sales-statistics module's
      // `query_period` split instead of firing two separate requests.
      return this.customerStatisticsService.loadComparison(
        params.number,
        params.subNumber,
        params.primary,
        params.reference
      );
    },
  });

  /** Default statistics window: year-to-date (Jan 1 of the current year),
   *  mirroring the sales-statistics module's default date range. The `to`
   *  bound defaults to today. */
  private defaultFrom(): IsoDate {
    const year = new Date().getFullYear();
    return `${year}-01-01` as IsoDate;
  }

  /** The Umsatzkennzahlen come from the statistics service, which only exists
      where the customer runs the Auswertungsdatenbank (ClickUp 869erubzn) — a
      failed query is how its absence shows, so the tiles disappear instead of
      claiming 0 €. An empty result is NOT an absence: a customer without
      revenue in the period legitimately reads 0. */
  readonly customerStatisticsAvailable = computed(
    () => this.statisticsResource.status() !== 'error'
  );

  readonly customerStatisticsSeries = computed(
    () => this.statisticsResource.value()?.series ?? []
  );
  readonly customerStatisticsLoading = computed(
    () => this.statisticsResource.status() === 'loading'
  );

  readonly customerRevenue = computed(
    () => this.statisticsResource.value()?.current.netto ?? 0
  );
  readonly customerDb = computed(() => this.statisticsResource.value()?.current.db ?? 0);
  readonly customerDbPercent = computed(() => {
    const netto = this.customerRevenue();
    return netto !== 0 ? (this.customerDb() / netto) * 100 : 0;
  });
  readonly customerRevenueSparkline = computed<readonly number[]>(() =>
    this.customerStatisticsSeries().map((r) => r.netto)
  );
  readonly customerDbSparkline = computed<readonly number[]>(() =>
    this.customerStatisticsSeries().map((r) => r.db)
  );
  readonly customerDbPercentSparkline = computed<readonly number[]>(() =>
    this.customerStatisticsSeries().map((r) => r.dbPercent)
  );

  /** Prior-year revenue/DB for the "vs. prior year" KPI comparison — from the
   *  same single request as the current period. */
  readonly priorYearRevenue = computed<number | null>(
    () => this.statisticsResource.value()?.previous.netto ?? null
  );
  readonly priorYearDb = computed<number | null>(
    () => this.statisticsResource.value()?.previous.db ?? null
  );

  readonly formatPercent = (value: number): string =>
    `${new Intl.NumberFormat('de-AT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)} %`;

  /* ---- derived customer info ---- */

  readonly customerState = computed(() => this.customerDetail()?.state ?? null);

  readonly customerData = computed(() => {
    const detail = this.customerDetail();
    if (!detail) return null;
    const contact = detail.mainContact;
    const account = detail.account;
    return {
      number: account?.number,
      subnumber: account?.subnumber,
      name: account?.name,
      category: account?.category?.name,
      salutation: contact?.salutation,
      name1: contact?.lastName,
      name2: contact?.firstName,
      nameAffix: contact?.nameAffix,
      nameAffix2: contact?.nameAffix2,
      street: contact?.street,
      postCode: contact?.zipCode,
      city: contact?.location,
      countryCode: contact?.countryCode,
      email: contact?.mail1,
      email2: contact?.mail2,
      email3: contact?.mail3,
      url: contact?.url,
      phone1: contact?.telephoneNumberOrganisation,
      phone2: contact?.mobilePhoneNumberOrganisation,
      directDial: contact?.directDialOrganisation,
      phoneExtension: contact?.telephoneNumberExtension,
      fax: contact?.faxOrganisation,
      vatId: detail.vatId,
      accountingBalance: detail.accountingBalance,
      creditFrame1: detail.creditFrame1,
      creditFrame2: detail.creditFrame2,
      mainGroup: detail.customerGroup?.mainGroup,
      mainGroupDescription: detail.customerGroup?.mainGroupDescription,
      subGroup: detail.customerGroup?.subGroup,
      subGroupDescription: detail.customerGroup?.subGroupDescription,
      subGroup1: detail.customerGroup?.subGroup1,
      subGroup1Description: detail.customerGroup?.subGroupDescription,
    };
  });

  readonly customerMapsUrl = computed(() => {
    if (this.isEmbeddedHost) return null;
    const d = this.customerData();
    if (!d) return null;
    const parts = [d.street, d.postCode, d.city].filter((p) => !!p?.trim());
    if (parts.length === 0) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
  });

  readonly customerDeliveryAddress = computed(() => {
    const a = this.customerDetail()?.deliveryAddress;
    if (!a) return null;
    const hasContent = [a.name1, a.name2, a.name3, a.street, a.postCode, a.city].some(
      (v) => !!v?.trim()
    );
    if (!hasContent) return null;
    return a;
  });

  /** Google-Maps URL for the delivery address (null when no usable address). */
  readonly deliveryMapsUrl = computed(() => {
    if (this.isEmbeddedHost) return null;
    const a = this.customerDeliveryAddress();
    if (!a) return null;
    const parts = [a.street, a.postCode, a.city].filter((p) => !!p?.trim());
    if (parts.length === 0) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
  });

  readonly customerDeliveryCustomersExist = computed(
    () =>
      (this.customerDetail() as { deliveryCustomersExist?: boolean } | null)
        ?.deliveryCustomersExist ?? false
  );

  readonly customerDocumentType = computed(() => {
    const dt = this.customerDetail()?.documentType as
      { key?: string; name?: string } | null | undefined;
    if (!dt) return null;
    return dt.name?.trim() || dt.key?.trim() || null;
  });

  readonly customerGroupLabel = computed(() => {
    const group = this.customerDetail()?.customerGroup;
    if (!group) return null;
    return group.mainGroupDescription?.trim() || group.mainGroup?.trim() || null;
  });

  readonly customerInitials = computed(() => {
    const d = this.customerData();
    const first = d?.name2?.trim()?.[0] ?? '';
    const last = d?.name1?.trim()?.[0] ?? '';
    const initials = (first + last).toUpperCase();
    if (initials) return initials;
    return d?.name?.trim()?.slice(0, 2).toUpperCase() ?? '';
  });

  readonly customerMasterData = computed(() => {
    const detail = this.customerDetail();
    if (!detail) return null;
    const fmt = (v: { key?: string; name?: string } | null | undefined): string => {
      if (!v) return '—';
      const name = v.name?.trim();
      const key = v.key?.trim();
      if (name && key) return `${key} — ${name}`;
      return name || key || '—';
    };
    return {
      blocked: detail['state']?.blocked ?? false,
      inactive: detail['state']?.inactive ?? false,
      deleted: detail['state']?.deleted ?? false,
      accountCategory: detail.account?.category?.name ?? '—',
      termsOfPayment: fmt(detail.termsOfPayment),
      termsCashDiscount1: detail.termsOfPayment?.cashDiscount1 ?? null,
      termsCashDiscount2: detail.termsOfPayment?.cashDiscount2 ?? null,
      termsDueDays: detail.termsOfPayment?.dueDays ?? null,
      termsDays1: detail.termsOfPayment?.days1 ?? null,
      termsDays2: detail.termsOfPayment?.days2 ?? null,
      priceLevel: fmt(detail.priceLevel),
      conditionSheet: fmt(detail.conditionSheet),
      groupPriceLevel: fmt(detail.customerGroup?.priceLevel),
      groupConditionSheet: fmt(detail.customerGroup?.conditionSheet),
      paymentMethod: fmt(detail.paymentMethod),
      paymentMethod2: fmt(detail.paymentMethod2),
      paymentInterval: fmt(
        detail.paymentInterval as { key?: string; name?: string } | null
      ),
      representative1: detail.representative
        ? `${detail.representative.firstName ?? ''} ${detail.representative.lastName ?? ''}`.trim() ||
          '—'
        : '—',
      representative2: detail.representative2
        ? `${detail.representative2.firstName ?? ''} ${detail.representative2.lastName ?? ''}`.trim() ||
          '—'
        : '—',
      edifactGroup: fmt(detail.customerEdifactGroup),
      edifactGroup2: fmt(detail.customerEdifactGroup2),
      gln: detail.gln || '—',
      glnCentral: detail.glnCentral || '—',
      orderBbn: detail.orderBbn || '—',
      orderBbn13: detail.orderBbn13 || '—',
      taxVatId: detail.vatId || '—',
      agriculturalBusinessNumber: detail.agriculturalBusinessNumber || '—',
      deliveryNumber: detail.deliveryNumber || '—',
      deliveryNotePrint: fmt(
        detail.deliveryNotePrint as { key?: string; name?: string } | null
      ),
      deliveryNotesInvoicingSettings: fmt(
        detail.deliveryNotesInvoicingSettings as { key?: string; name?: string } | null
      ),
      externalPurchaseOrderNumber: detail.externalPurchaseOrderNumber || '—',
      purchaseSupplier: detail.purchaseSupplier
        ? `${detail.purchaseSupplier.number ?? ''} — ${detail.purchaseSupplier.name ?? ''}`.trim() ||
          '—'
        : '—',
      accountingBalance: detail.accountingBalance,
      creditFrame1: detail.creditFrame1,
      creditFrame2: detail.creditFrame2,
      insuranceIndicator: fmt(
        detail.insuranceIndicator as { key?: string; name?: string } | null
      ),
      insuranceIndicator2: fmt(
        detail.insuranceIndicator2 as { key?: string; name?: string } | null
      ),
      indicators: [
        detail.customerIndicator1,
        detail.customerIndicator2,
        detail.customerIndicator3,
        detail.customerIndicator4,
        detail.customerIndicator5,
        detail.customerIndicator6,
        detail.customerIndicator7,
        detail.customerIndicator8,
        detail.customerIndicator9,
      ]
        .map((v, i) => ({ index: i + 1, value: fmt(v) }))
        .filter((e) => e.value !== '—'),
      summations: [
        detail.summation1,
        detail.summation2,
        detail.summation3,
        detail.summation4,
        detail.summation5,
        detail.summation6,
        detail.summation7,
        detail.summation8,
        detail.summation9,
        detail.summation10,
      ]
        .map((v, i) => ({ index: i + 1, value: v ?? null }))
        .filter((e) => e.value !== null && e.value !== 0),
      terms: [
        detail.term1,
        detail.term2,
        detail.term3,
        detail.term4,
        detail.term5,
        detail.term6,
        detail.term7,
        detail.term8,
        detail.term9,
        detail.term10,
      ]
        .map((v, i) => ({ index: i + 1, value: v ?? '' }))
        .filter((e) => e.value && e.value !== '0'),
    };
  });

  constructionSiteAddress(site: MasterdataCustomer): string | null {
    const street = [site.street, site.streetNumber]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' ');
    const cityLine = [site.postCode, site.city]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' ');
    const result = [street, cityLine].filter(Boolean).join(', ');
    return result.length > 0 ? result : null;
  }

  constructionSiteName(site: MasterdataCustomer): string {
    return site.name1?.trim() || site.shortName?.trim() || '—';
  }

  constructionSiteMapsUrl(site: MasterdataCustomer): string | null {
    if (this.isEmbeddedHost) return null;
    const addr = this.constructionSiteAddress(site);
    if (!addr) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  }

  /** Open the construction site's address in Google Maps in a new tab.
   *  Stops propagation so the row's create-Beleg click does not also fire. */
  openSiteInMaps(site: MasterdataCustomer, event: Event): void {
    event.stopPropagation();
    const url = this.constructionSiteMapsUrl(site);
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  /** Both construction-site endpoints are addressed by the INVOICING customer.
   *  TODO: the masterdata list filters the main account out (customerSubnumber != 0),
   *  so when the panel shows a construction site itself the fallback id is the
   *  sub-customer — needs the invoicing id from the backend. */
  private readonly invoicingCustomerId = computed<string | null>(() => {
    const mainRow = this.customerConstructionSites().find(
      (site) => !site.customerSubnumber
    );
    return mainRow?.id != null ? String(mainRow.id) : this.customerId();
  });

  createConstructionSite(): void {
    void this.openConstructionSiteForm(null);
  }

  /** Edit a site. Stops propagation so the row's create-Beleg click stays put. */
  editConstructionSite(site: MasterdataCustomer, event: Event): void {
    event.stopPropagation();
    if (site.id == null) return;
    void this.openConstructionSiteForm(String(site.id));
  }

  /** Imported lazily so the form (and its date picker) stays off the panel's
   *  initial chunk. Resolves with the site's `customerId`; a dismissal resolves
   *  with the string `'DISMISSED'`, which has to be filtered explicitly. */
  private async openConstructionSiteForm(
    constructionSiteId: string | null
  ): Promise<void> {
    const customerId = this.invoicingCustomerId();
    if (!customerId) return;
    const { ConstructionSiteFormComponent: component } = await import(
      '../construction-site-form/construction-site-form.component'
    );
    const result = await this.modal<ConstructionSiteFormComponent>({
      component,
      title: constructionSiteId ? 'Baustelle ändern' : 'Baustelle erstellen',
      icon: 'fa-regular fa-person-digging',
      separateContentSection: true,
      dismiss: true,
      data: { customerId, constructionSiteId },
    });
    if (result === 'DISMISSED' || !result) return;
    this.loadConstructionSites(this.siteQuery());
    this.constructionSiteSaved.emit(result);
  }
}
